import type { OutboxItem } from '@Domain/Ports/Outbox/OutboxPort';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import useOutboxPort from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/useOutboxPort';
import { css } from '@emotion/react';
import * as React from 'react';
import { Button, ConfirmDeleteDialog } from '@verclam/ui-kit';

const errorMessageStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: inherit;
`;

const actionsRowStyle = css`
	display: flex;
	gap: 0.5rem;
	flex-wrap: wrap;
	margin-top: 0.25rem;
`;

type Props = {
	readonly item: OutboxItem;
	/** Already formatted `JJ/MM/AAAA` — the `confirmText` the discard dialog asks back. */
	readonly conductedOnDisplay: string;
	/** Whether the item belongs to a note-taking: only the dialog's wording differs. */
	readonly isNotes: boolean;
	/**
	 * Whether the composite interview already exists server-side. Once it does, an abandoned transfer
	 * would leave the server holding an interview with no recording that no route can ever delete —
	 * an orphan this leaf would otherwise make reachable. Abandon therefore stops being offered from here the moment anything has left the
	 * device: this only masks the delete action, it never masks "Réessayer". Defaults to `false`.
	 */
	readonly hasServerInterview?: boolean;
	/**
	 * Called once `discard()` has actually resolved. `discard()` is the only door out of the queue that
	 * is not a successful transfer: without this callback, the local interview behind this item would
	 * survive forever — its participants still readable in clear text on the device — since an item
	 * leaving the queue does not trigger a reconciliation.
	 */
	readonly onDiscarded?: () => void;
};

/**
 * Leaf action component for a failed queue item: it reads the item's own identifier and dispatches
 * `retryNow`/`discard` on the outbox port itself — no `onRetry`/`onDiscard` callback. `discard`
 * deletes an unconfirmed recording for good, so it is gated behind a `ConfirmDeleteDialog` that asks for
 * the interview's date back — and is only ever offered while `hasServerInterview` is `false`.
 */
const OutboxItemCardActions: React.FC<Props> = ({
	item,
	conductedOnDisplay,
	isNotes,
	hasServerInterview = false,
	onDiscarded,
}) => {
	const outboxPort = useOutboxPort();
	const [isDiscardConfirmVisible, setIsDiscardConfirmVisible] = React.useState(false);
	const [actionErrorMessage, setActionErrorMessage] = React.useState<string | null>(null);

	const handleRetryClick = React.useCallback(() => {
		setActionErrorMessage(null);
		outboxPort.retryNow(item.id).catch((reason: unknown) => {
			setActionErrorMessage(
				reason instanceof Error ? reason.message : 'La nouvelle tentative a échoué.',
			);
		});
	}, [outboxPort, item.id]);

	const handleOpenDiscardConfirm = React.useCallback(() => {
		setIsDiscardConfirmVisible(true);
	}, []);

	const handleDiscardConfirmHide = React.useCallback(() => {
		setIsDiscardConfirmVisible(false);
	}, []);

	const handleDiscardAccept = React.useCallback(() => {
		setActionErrorMessage(null);
		outboxPort
			.discard(item.id)
			.then(() => {
				onDiscarded?.();
			})
			.catch((reason: unknown) => {
				setActionErrorMessage(reason instanceof Error ? reason.message : 'La suppression a échoué.');
			});
		// Closes immediately regardless of the outcome above: waiting for the call to settle would
		// leave the dialog open long enough for a second click.
		setIsDiscardConfirmVisible(false);
	}, [outboxPort, item.id, onDiscarded]);

	return (
		<React.Fragment>
			<ConditionalLoader data={actionErrorMessage}>
				{(message) => <p css={errorMessageStyle}>{message}</p>}
			</ConditionalLoader>
			<div css={actionsRowStyle}>
				<Button
					type="button"
					size="sm"
					severity="secondary"
					label="Réessayer"
					onClick={handleRetryClick}
				/>
				<ConditionalLoader data={!hasServerInterview}>
					<Button
						type="button"
						size="sm"
						severity="danger"
						label="Supprimer l'enregistrement"
						onClick={handleOpenDiscardConfirm}
					/>
				</ConditionalLoader>
			</div>
			<ConditionalLoader data={!hasServerInterview}>
				<ConfirmDeleteDialog
					visible={isDiscardConfirmVisible}
					onHide={handleDiscardConfirmHide}
					header="Supprimer cet enregistrement"
					message={
						isNotes
							? `L'enregistrement de la prise de notes du ${conductedOnDisplay} sera définitivement supprimé de cet appareil. Cette action est irréversible. Saisissez la date de la prise de notes pour confirmer.`
							: `L'enregistrement de l'entretien du ${conductedOnDisplay} sera définitivement supprimé de cet appareil. Cette action est irréversible. Saisissez la date de l'entretien pour confirmer.`
					}
					confirmText={conductedOnDisplay}
					acceptLabel="Supprimer définitivement"
					rejectLabel="Annuler"
					acceptCallback={handleDiscardAccept}
				/>
			</ConditionalLoader>
		</React.Fragment>
	);
};

export default OutboxItemCardActions;
